/**
 * @file Route table (1.13.2, 1.13.6).
 *
 * Four routes inside one shell, and three of the four paths are decided by
 * something other than this file:
 *
 *   /             the page. Everything happens here.
 *   /auth/verify  where the emailed magic link lands. The API builds that URL
 *                 as <origin>/auth/verify, so the path is a contract with
 *                 ecommerce-api and not a choice made here.
 *   /account      where Stripe returns the customer, success or cancel. Also
 *                 built by the API, as <origin>/account.
 *   /membership   where a member stops renewing or changes their card
 *                 (1.13.2). Every membership email the API sends for a
 *                 membership bought here links to <origin>/membership
 *                 (its MEMBERSHIP_MANAGE_PATHS, 2026-11-04), so this path
 *                 is a contract with ecommerce-api too.
 *
 * Anything else redirects to the page rather than rendering a not-found
 * screen. On a one-page site a mistyped path is somebody who wants the offer,
 * and there is nothing else here to send them to.
 *
 * They are children of a layout route so the header mounts once. Nested
 * rather than repeated in each screen, which also stops the brand remounting
 * and flickering when /auth/verify redirects back to the offer.
 *
 * A failure inside any of them renders `ErrorFallback` beneath the header,
 * which also reports it (F1.9). The boundary sits on a pathless route inside
 * the shell rather than on the shell itself, so the brand stays on screen.
 *
 * Routes are lazy so the emailed-link and post-payment screens, which most
 * visitors never see, stay out of the bundle that renders the first paint.
 */
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';

import { ErrorFallback } from '../routes/system/ErrorFallback';

import { AppShell } from './layout/AppShell';

import type { ReactElement } from 'react';

/** The routes that render inside the shell. */
export const routes = [
  {
    path: '/',
    lazy: () => import('../routes/landing/Landing'),
  },
  {
    path: '/auth/verify',
    lazy: () => import('../routes/verify/Verify'),
  },
  {
    path: '/account',
    lazy: () => import('../routes/account/Account'),
  },
  {
    path: '/membership',
    lazy: () => import('../routes/membership/Membership'),
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
];

/** The shell, with every route beneath it and one error boundary around them all. */
export const routeTree = [
  { element: <AppShell />, children: [{ ErrorBoundary: ErrorFallback, children: routes }] },
];

const router = createBrowserRouter(routeTree);

/** Mounts the router. */
export function AppRouter(): ReactElement {
  return <RouterProvider router={router} />;
}
